import { useEffect, useRef, useState } from "react";
import { Pencil, Plus, Trash2, Handshake, ArrowUp, ArrowDown, FileDown, Upload, Loader2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import ImageUpload from "@/components/ImageUpload";

const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";
const EMPTY = { name: "", description: "", url: "", logo_url: "", position: 0 };

function MediaKitCard() {
  const [kit, setKit] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = () => api.get("/media-kit").then((r) => setKit(r.data?.url ? r.data : null)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media-kit", fd);
      setKit(data);
      toast.success("Media kit mis en ligne — le bouton est visible sur la page Partenaires");
    } catch (err) { toast.error(errMsg(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async () => {
    if (!window.confirm("Retirer le media kit ? Le bouton disparaîtra de la page Partenaires.")) return;
    try {
      await api.delete("/admin/media-kit");
      setKit(null);
      toast.success("Media kit retiré");
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="card-tactical grid gap-3 p-5" data-testid="admin-media-kit">
      <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
        <FileDown className="h-5 w-5 text-orange-500" /> Media kit partenaires
      </p>
      <p className="text-xs text-slate-500">
        PDF ou ZIP (max 25 Mo) — présentation de la structure, audiences et offres de sponsoring.
        Un bouton « Télécharger le media kit » apparaît sur la page Partenaires dès qu'un fichier est en ligne.
      </p>
      {kit ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3" data-testid="admin-media-kit-current">
          <FileDown className="h-5 w-5 text-emerald-400" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{kit.filename}</p>
            <p className="text-xs text-slate-400">{(kit.size / 1024 / 1024).toFixed(2)} Mo — mis à jour le {new Date(kit.updated_at).toLocaleDateString("fr-FR")}</p>
          </div>
          <a href={kit.url} target="_blank" rel="noreferrer" data-testid="admin-media-kit-view"
            className="rounded-lg border border-slate-600 px-3 py-1.5 text-xs font-bold uppercase text-slate-300 hover:border-orange-500 hover:text-orange-400">
            Voir
          </a>
          <button onClick={remove} data-testid="admin-media-kit-delete"
            className="rounded-lg border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
        </div>
      ) : (
        <p className="text-sm text-slate-500" data-testid="admin-media-kit-empty">Aucun media kit en ligne pour le moment.</p>
      )}
      <input ref={inputRef} type="file" accept=".pdf,.zip" className="hidden" onChange={upload} data-testid="admin-media-kit-input" />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="admin-media-kit-upload"
        className="flex w-fit items-center gap-2 rounded-lg bg-orange-500 px-4 py-2 text-sm font-bold uppercase text-[#060B18] transition-all hover:bg-orange-400 disabled:opacity-50">
        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {uploading ? "Envoi en cours…" : (kit ? "Remplacer le fichier" : "Déposer le media kit")}
      </button>
    </div>
  );
}

export default function PartnersTab() {
  const [partners, setPartners] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/partners").then((r) => setPartners(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/partners/${editing}`, form);
        toast.success("Partenaire mis à jour");
      } else {
        await api.post("/admin/partners", form);
        toast.success("Partenaire ajouté");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer le partenaire ${p.name} ?`)) return;
    try {
      await api.delete(`/admin/partners/${p.id}`);
      toast.success("Partenaire supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const move = async (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= partners.length) return;
    const a = partners[i];
    const b = partners[j];
    const payload = (x, position) => ({ name: x.name, description: x.description || "", url: x.url || "", logo_url: x.logo_url || "", position });
    try {
      await api.put(`/admin/partners/${a.id}`, payload(a, b.position ?? j + 1));
      await api.put(`/admin/partners/${b.id}`, payload(b, a.position ?? i + 1));
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-6" data-testid="partners-admin">
      <MediaKitCard />
      <form onSubmit={submit} className="card-tactical grid gap-4 p-5" data-testid="admin-partner-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          <Handshake className="h-5 w-5 text-orange-500" /> {editing ? "Modifier le partenaire" : "Ajouter un partenaire"}
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <input placeholder="Nom du partenaire" value={form.name} data-testid="admin-partner-name"
            onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} required />
          <input placeholder="Lien du site (https://...)" value={form.url} data-testid="admin-partner-url"
            onChange={(e) => setForm({ ...form, url: e.target.value })} className={inputCls} />
        </div>
        <textarea placeholder="Description (qui sont-ils, que vous apportent-ils...)" value={form.description}
          data-testid="admin-partner-description" rows={2}
          onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
        <div>
          <input placeholder="URL du logo (optionnel)" value={form.logo_url}
            onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className={inputCls} />
          <div className="mt-2">
            <ImageUpload testid="admin-partner-logo-upload" onUploaded={(url) => setForm((f) => ({ ...f, logo_url: url }))} />
          </div>
        </div>
        <div className="flex gap-3">
          <button type="submit" data-testid="admin-partner-submit"
            className="flex items-center gap-2 rounded-lg bg-orange-500 px-5 py-2 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
            <Plus className="h-4 w-4" /> {editing ? "Mettre à jour" : "Ajouter"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="admin-partner-cancel"
              className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-bold uppercase text-slate-300 hover:border-orange-500">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="grid gap-3">
        {partners.length === 0 && <p className="text-sm text-slate-500">Aucun partenaire. Ajoutez-en un pour le voir apparaître sur la page Partenaires.</p>}
        {partners.map((p, i) => (
          <div key={p.id} data-testid={`admin-partner-row-${p.id}`} className="card-tactical flex flex-wrap items-center gap-4 p-4">
            {p.logo_url && <img src={p.logo_url} alt={`Logo ${p.name}`} className="h-10 w-16 rounded bg-[#060B18] object-contain" />}
            <div className="min-w-[150px] flex-1">
              <p className="font-heading text-lg font-bold uppercase">{p.name}</p>
              {p.url && <p className="truncate text-xs text-slate-400">{p.url}</p>}
            </div>
            <div className="flex gap-2">
              <button onClick={() => move(i, -1)} disabled={i === 0} data-testid={`admin-partner-up-${p.id}`} aria-label="Monter"
                className="rounded-lg border border-slate-600 p-2 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
              <button onClick={() => move(i, 1)} disabled={i === partners.length - 1} data-testid={`admin-partner-down-${p.id}`} aria-label="Descendre"
                className="rounded-lg border border-slate-600 p-2 text-slate-300 transition-colors hover:border-orange-500 hover:text-orange-400 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
              <button onClick={() => { setEditing(p.id); setForm({ name: p.name, description: p.description || "", url: p.url || "", logo_url: p.logo_url || "", position: p.position ?? i + 1 }); }}
                data-testid={`admin-partner-edit-${p.id}`}
                className="rounded-lg border border-orange-500/40 p-2 text-orange-400 hover:bg-orange-500/10"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(p)} data-testid={`admin-partner-delete-${p.id}`}
                className="rounded-lg border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
